import { useCallback, useMemo, useRef } from 'react'

import { useQueryClient } from 'react-query'

import {
  FlowData,
  FlowEvents,
  FlowInitialProps,
  FlowQueriesToInvalidate,
  FlowQueriesToRemove,
} from '@cash-web/shared-flow-data-access-context'

export type FlowListener = FlowEvents &
  FlowQueriesToInvalidate &
  FlowQueriesToRemove & {
    shouldRevalidateRoute?: boolean
  }

const getListenerData = (data: FlowData | undefined) => {
  if (
    data == null ||
    (data.onCompleteFlow == null &&
      data.onCancelFlow == null &&
      data.queriesToInvalidate == null &&
      data.shouldRevalidateRoute == null)
  ) {
    return undefined
  }
  return {
    onCompleteFlow: data.onCompleteFlow,
    onCancelFlow: data.onCancelFlow,
    queriesToInvalidate: data.queriesToInvalidate,
    queriesToRemove: data.queriesToRemove,
    shouldRevalidateRoute: data.shouldRevalidateRoute,
  }
}

export type UseFlowListenersProps = {
  initialFlow?: FlowInitialProps
  onRevalidateRoute?: () => void
}

export const useFlowListeners = ({ initialFlow, onRevalidateRoute }: UseFlowListenersProps) => {
  const initialListener = initialFlow ? getListenerData(initialFlow) : undefined

  const queryClient = useQueryClient()
  const childFlowListener = useRef<FlowListener[]>(initialListener ? [initialListener] : [])
  const suspended = useRef<Record<string, FlowListener[]>>({})

  const clear = useCallback(() => {
    childFlowListener.current = initialListener ? [initialListener] : []
  }, [initialListener])

  const reset = useCallback(() => {
    clear()
    suspended.current = {}
  }, [clear])

  const invalidateQueries = useCallback(async () => {
    for (const listener of childFlowListener.current) {
      if (listener.queriesToInvalidate === 'ALL') {
        queryClient.invalidateQueries()
      } else {
        await Promise.all(listener.queriesToInvalidate.map(queryKey => queryClient.invalidateQueries(queryKey)))
      }
    }
  }, [queryClient])

  const removeQueries = useCallback(async () => {
    for (const listener of childFlowListener.current) {
      if (listener.queriesToRemove) {
        await Promise.all(listener.queriesToRemove.map(queryKey => queryClient.removeQueries(queryKey)))
      }
    }
  }, [queryClient])

  const onCompleteFlow = useCallback(async () => {
    void invalidateQueries()
    void removeQueries()
    let revalidationComplete = false
    for (const listener of childFlowListener.current) {
      listener.onCompleteFlow?.()
      if (!revalidationComplete && listener.shouldRevalidateRoute) {
        revalidationComplete = true
        void onRevalidateRoute?.()
      }
    }
    clear()
  }, [invalidateQueries, removeQueries, clear, onRevalidateRoute])

  const onCancelFlow = useCallback(async () => {
    void invalidateQueries()
    void removeQueries()
    let revalidationComplete = false
    for (const listener of childFlowListener.current) {
      listener.onCancelFlow?.()
      if (!revalidationComplete && listener.shouldRevalidateRoute) {
        revalidationComplete = true
        void onRevalidateRoute?.()
      }
    }
    clear()
  }, [invalidateQueries, removeQueries, clear, onRevalidateRoute])

  const add = useCallback((data: FlowData) => {
    const listener = getListenerData(data)

    if (listener == null) {
      return
    }

    childFlowListener.current = [...childFlowListener.current, listener]
  }, [])

  const suspend = useCallback((id: string) => {
    suspended.current[id] = childFlowListener.current
    childFlowListener.current = []
  }, [])

  const restore = useCallback((id: string) => {
    const { [id]: next, ...rest } = suspended.current
    if (next) {
      childFlowListener.current = next
      suspended.current = rest
    }
  }, [])

  return useMemo(
    () => ({ add, suspend, restore, reset, onCompleteFlow, onCancelFlow, invalidateQueries }),
    [add, suspend, restore, reset, invalidateQueries, onCancelFlow, onCompleteFlow]
  )
}
